import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowRight,
  Loader2,
  MessageCircleHeart,
  Compass,
  Send,
  NotebookPen,
  BookmarkPlus,
  Trash2,
  CheckCircle2,
  Wand2,
} from "lucide-react";
import { guidance as guidanceApi, coach as coachApi, analyze as analyzeApi, guest as guestApi } from "../lib/api";
import {
  ensureGuest,
  getGuest,
  appendExchange,
  recentTurns,
  isTrialActive,
  trialDaysLeft,
  trialHoursLeft,
  addJournalScratch,
  listJournalScratch,
  removeJournalScratch,
  addWisdomScratch,
  listWisdomScratch,
  removeWisdomScratch,
} from "../lib/guest";
import GuidanceResponse from "../components/GuidanceResponse";
import ReturnToProverb from "../components/ReturnToProverb";

const EXAMPLE_SCENARIOS = [
  "I'm stressed about a decision I need to make and I'm not sure what to do.",
  "I just yelled at my kids and feel like a terrible parent.",
  "I need to text my mom about missing thanksgiving without hurting her.",
  "I feel lost and don't know what to do next.",
];

const TABS = [
  { id: "guidance", label: "Immediate Guidance", icon: Compass },
  { id: "coach", label: "Talk with Coach", icon: MessageCircleHeart },
  { id: "say-it-better", label: "Say It Better", icon: Wand2 },
  { id: "journal", label: "Journal", icon: NotebookPen },
  { id: "wisdom", label: "Saved Wisdom", icon: BookmarkPlus },
];

export default function GuestPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Authenticated session ALWAYS wins over anonymous guest state — a
  // logged-in visitor must never see the guest trial-ended gate just
  // because stale `lg_guest` localStorage says the anonymous trial has
  // expired. If a token is present, kick them straight into the auth
  // equivalent of whatever tab they asked for.
  const _authToken =
    typeof window !== "undefined" ? localStorage.getItem("lg_token") : null;
  useEffect(() => {
    if (!_authToken) return;
    const requested = params.get("tab");
    if (requested === "say-it-better") {
      navigate("/analyze", { replace: true });
    } else if (requested === "coach") {
      navigate("/coach", { replace: true });
    } else if (requested === "journal") {
      navigate("/profile", { replace: true });
    } else {
      navigate("/home", { replace: true });
    }
  }, [_authToken, params, navigate]);

  const [state, setState] = useState(() => ensureGuest());
  const [tab, setTab] = useState(() => {
    const t = params.get("tab");
    return TABS.some((x) => x.id === t) ? t : "guidance";
  });

  // Guidance panel state
  const [gText, setGText] = useState("");
  const [gResult, setGResult] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const [gFollowBusy, setGFollowBusy] = useState(false);
  // Coach panel state
  const [cInput, setCInput] = useState("");
  const [cBusy, setCBusy] = useState(false);
  const chatEndRef = useRef(null);
  // Say It Better prefill from guidance action
  const [saybPrefill, setSaybPrefill] = useState(null); // {original, draft}
  // Journal + Wisdom scratch
  const [journalDraft, setJournalDraft] = useState({ title: "", content: "" });
  const [journalList, setJournalList] = useState(listJournalScratch());
  const [wisdomList, setWisdomList] = useState(listWisdomScratch());

  const trialActive = isTrialActive();
  const daysLeft = trialDaysLeft();
  const hoursLeft = trialHoursLeft();

  useEffect(() => {
    if (!sessionStorage.getItem("lg_guest_visit_sent") && state?.id) {
      guestApi.track({
        event_type: "guest_visit",
        guest_id: state.id,
        from_proverb: !!state.from_proverb,
        proverb_topic: state.proverb_topic || null,
      });
      sessionStorage.setItem("lg_guest_visit_sent", "1");
    }
  }, [state?.id, state?.from_proverb, state?.proverb_topic]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [state?.exchanges?.length, tab]);

  // Scroll to top when switching to Say It Better (per spec).
  useEffect(() => {
    if (tab === "say-it-better") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [tab]);

  const chatHistory = useMemo(
    () => (state?.exchanges || []).filter((e) => e.kind === "coach"),
    [state?.exchanges]
  );

  if (_authToken) {
    // Authenticated user redirect in progress (see useEffect above) —
    // render an empty canvas so we never paint the TrialEndedGate.
    return (
      <div
        className="min-h-screen bg-[#F9FAF7]"
        data-testid="guest-auth-redirecting"
      />
    );
  }

  if (!trialActive) {
    return <TrialEndedGate />;
  }

  // --- Guidance handlers ---
  const runGuidance = async (e) => {
    e?.preventDefault?.();
    if (gBusy) return;
    const t = gText.trim();
    if (t.length < 4) {
      toast.error("Share a little more so LifeGuide can meet you where you are.");
      return;
    }
    setGBusy(true);
    try {
      const r = await guidanceApi.sample(t);
      setGResult(r);
      const next = appendExchange({ role: "user", kind: "guidance", content: t });
      appendExchange({
        role: "assistant",
        kind: "guidance",
        content: r?.follow_up_question || r?.next_step || "",
      });
      setState(next || getGuest());
      guestApi.track({
        event_type: "guest_guidance",
        guest_id: state.id,
        from_proverb: !!state.from_proverb,
      });
      requestAnimationFrame(() => {
        document
          .getElementById("guest-guidance-result")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went sideways — please try again in a moment.");
    } finally {
      setGBusy(false);
    }
  };

  const runGuidanceFollowUp = async (text) => {
    if (gFollowBusy) return;
    setGFollowBusy(true);
    try {
      const history = recentTurns("guidance", 8);
      // Feed the previous exchange + the follow-up as one continuation.
      const r = await guidanceApi.sample(text, history);
      appendExchange({ role: "user", kind: "guidance", content: text });
      appendExchange({
        role: "assistant",
        kind: "guidance",
        content: r?.follow_up_question || r?.next_step || "",
      });
      setState(getGuest());
      setGResult(r);
      guestApi.track({
        event_type: "guest_guidance",
        guest_id: state.id,
        from_proverb: !!state.from_proverb,
      });
      requestAnimationFrame(() => {
        document
          .getElementById("guest-guidance-result")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went sideways — please try again in a moment.");
    } finally {
      setGFollowBusy(false);
    }
  };

  const handleGuidanceAction = (action) => {
    if (action === "continue_talking") {
      // Route into the Coach tab with the follow-up seed as the first message.
      const seed = gResult?.follow_up_question || gResult?.next_step || "";
      if (seed) setCInput(seed);
      setTab("coach");
      return;
    }
    if (action === "make_plan") {
      const seed = `Help me build a concrete plan based on this: ${gResult?.next_step || ""}`;
      setCInput(seed);
      setTab("coach");
      return;
    }
    if (action === "journal_this") {
      const q = gText || "";
      const parts = [];
      if (q) parts.push(`What I shared:\n${q}`);
      if (gResult?.what_im_hearing || gResult?.feeling)
        parts.push(`What I'm hearing:\n${gResult.what_im_hearing || gResult.feeling}`);
      if (gResult?.wisdom_for_moment || gResult?.biblical_perspective)
        parts.push(
          `Wisdom for this moment:\n${gResult.wisdom_for_moment || gResult.biblical_perspective}${
            gResult.scripture_reference ? `\n— ${gResult.scripture_reference}` : ""
          }`
        );
      if (gResult?.next_step) parts.push(`Next step:\n${gResult.next_step}`);
      setJournalDraft({
        title: q ? q.slice(0, 80) : "Guidance moment",
        content: parts.join("\n\n"),
      });
      setTab("journal");
      toast.success("Draft moved to your Journal scratch — sign up to save it forever.");
      return;
    }
    if (action === "save_wisdom") {
      const title = (gText || "").slice(0, 100) || "Guidance moment";
      const body = [
        gResult?.wisdom_for_moment || gResult?.biblical_perspective || "",
        gResult?.next_step ? `Next step: ${gResult.next_step}` : "",
      ]
        .filter(Boolean)
        .join("\n\n");
      const next = addWisdomScratch({
        title,
        body,
        reference: gResult?.scripture_reference || null,
      });
      setWisdomList(next?.wisdom_scratch || listWisdomScratch());
      toast.success("Saved to your Wisdom scratch.");
      return;
    }
    if (action === "say_it_better") {
      setSaybPrefill(
        gResult?.say_it_better_prefill || {
          original: gText || "",
          draft: gResult?.better_response || "",
        }
      );
      setTab("say-it-better");
      return;
    }
  };

  // --- Coach ---
  const runCoach = async (e) => {
    e?.preventDefault?.();
    if (cBusy) return;
    const msg = cInput.trim();
    if (msg.length < 2) return;
    setCBusy(true);
    const withUser = appendExchange({ role: "user", kind: "coach", content: msg });
    setState(withUser);
    setCInput("");
    try {
      const localHistory = (withUser?.exchanges || [])
        .filter((e) => e.kind === "coach")
        .map((e) => ({ role: e.role, content: e.content }));
      const r = await coachApi.sample({
        message: msg,
        guest_id: state.id,
        history: localHistory.slice(-12),
      });
      const withReply = appendExchange({ role: "assistant", kind: "coach", content: r.reply });
      setState(withReply || getGuest());
      guestApi.track({
        event_type: "guest_coach",
        guest_id: state.id,
        from_proverb: !!state.from_proverb,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Coach is catching its breath — try again in a moment.");
    } finally {
      setCBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-[#2E2E2E]" data-testid="guest-page">
      <Header daysLeft={daysLeft} hoursLeft={hoursLeft} onCTA={() => onSignupClick(state)} fromProverb={!!state?.from_proverb} />

      {/* Context strip */}
      {state?.from_proverb && (
        <div className="max-w-4xl mx-auto px-6 pt-6" data-testid="guest-proverb-strip">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#E1ECDD] border border-[#4F6F52]/20 px-4 py-1.5">
            <MessageCircleHeart size={13} className="text-[#4F6F52]" />
            <span className="text-xs uppercase tracking-[0.16em] text-[#4F6F52] font-semibold">
              From I Need a Proverb
              {state.proverb_topic ? ` · ${state.proverb_topic}` : ""}
            </span>
          </div>
        </div>
      )}

      {/* Trial banner */}
      <div className="max-w-4xl mx-auto px-6 pt-6">
        <div
          data-testid="guest-trial-banner"
          className="rounded-2xl bg-gradient-to-br from-[#E1ECDD] to-[#F5EBD9]/50 border border-[#4F6F52]/25 px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2.5">
            <CheckCircle2 size={16} className="text-[#4F6F52]" strokeWidth={2} />
            <div>
              <div
                className="text-sm font-semibold text-[#2E4531]"
                data-testid="guest-trial-banner-title"
              >
                3-Day Pro Trial · All features unlocked
              </div>
              <div className="text-xs text-[#5C6E5C]" data-testid="guest-trial-banner-remaining">
                {daysLeft > 1 ? `${daysLeft} days left` : `${hoursLeft} hours left`} · no credit card required
              </div>
            </div>
          </div>
          <Link
            to="/signup?from=guest"
            data-testid="guest-trial-banner-cta"
            onClick={() => onSignupClick(state)}
            className="text-xs sm:text-sm bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-4 py-2 font-medium transition inline-flex items-center gap-1.5"
          >
            Save my progress <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-6 pt-6 overflow-x-auto">
        <div
          className="inline-flex rounded-full bg-white border border-[#DCE5DB] p-1 shadow-[0_2px_10px_rgba(0,0,0,0.03)]"
          role="tablist"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`guest-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                  active
                    ? "bg-[#4F6F52] text-white shadow-[0_4px_12px_rgba(79,111,82,0.22)]"
                    : "text-[#6B6B6B] hover:text-[#2E2E2E]"
                }`}
              >
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-6 pb-24">
        {tab === "guidance" && (
          <section data-testid="guest-guidance-panel" className="space-y-6">
            <div className="rounded-3xl bg-white border border-[#DCE5DB] p-6 sm:p-8 shadow-[0_6px_28px_rgba(0,0,0,0.04)]">
              <div className="text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-3 inline-flex items-center gap-2">
                <Compass size={13} /> For moments like
              </div>
              <ul className="space-y-1.5 text-[15px] text-[#2E2E2E] leading-relaxed mb-6">
                {EXAMPLE_SCENARIOS.map((s, i) => (
                  <li
                    key={i}
                    role="button"
                    data-testid={`guest-example-${i}`}
                    onClick={() => setGText(s)}
                    className="flex gap-2 items-start cursor-pointer hover:text-[#4F6F52] transition"
                  >
                    <span className="text-[#4F6F52] mt-1">·</span>
                    <span>"{s}"</span>
                  </li>
                ))}
              </ul>
              <form onSubmit={runGuidance} className="space-y-4">
                <label className="block text-sm text-[#6B6B6B]">
                  What's happening in your world right now?
                </label>
                <textarea
                  data-testid="guest-guidance-input"
                  rows={4}
                  maxLength={1000}
                  value={gText}
                  onChange={(e) => setGText(e.target.value)}
                  placeholder="Tap an example above or type your own. Everything stays private."
                  className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition resize-none text-[15px] leading-relaxed"
                />
                <button
                  type="submit"
                  disabled={gBusy || gText.trim().length < 4}
                  data-testid="guest-guidance-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-4 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
                >
                  {gBusy ? (
                    <>
                      <Loader2 size={15} className="animate-spin" /> LifeGuide is listening…
                    </>
                  ) : (
                    <>
                      Get guidance <ArrowRight size={15} strokeWidth={2} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {gResult && (
              <div id="guest-guidance-result">
                <GuidanceResponse
                  result={gResult}
                  onFollowUp={runGuidanceFollowUp}
                  onAction={handleGuidanceAction}
                  busy={gFollowBusy}
                  testidPrefix="guest-guidance"
                />
              </div>
            )}
          </section>
        )}

        {tab === "coach" && (
          <section data-testid="guest-coach-panel" className="space-y-4">
            <div className="rounded-3xl bg-white border border-[#DCE5DB] p-4 sm:p-6 shadow-[0_6px_28px_rgba(0,0,0,0.04)]">
              <div className="text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-3 inline-flex items-center gap-2">
                <MessageCircleHeart size={13} /> Talk with your Coach
              </div>
              <div
                className="min-h-[240px] max-h-[52vh] overflow-y-auto pr-1 space-y-3"
                data-testid="guest-coach-history"
              >
                {chatHistory.length === 0 && (
                  <div className="text-[15px] text-[#6B6B6B] italic px-1">
                    Start with whatever's on your mind — "I feel …", "Can you help me think about
                    …", or paste a message you're about to send.
                  </div>
                )}
                {chatHistory.map((m, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#EEF3EC] text-[#2E2E2E] border border-[#DCE5DB]"
                        : "bg-[#F9FAF7] text-[#2E2E2E] border border-[#DCE5DB]/60"
                    }`}
                    data-testid={`guest-coach-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                ))}
                {cBusy && (
                  <div className="rounded-2xl px-4 py-3 bg-[#F9FAF7] border border-[#DCE5DB]/60 text-[#6B6B6B] italic text-sm inline-flex items-center gap-2">
                    <Loader2 size={13} className="animate-spin" /> Coach is thinking…
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={runCoach} className="mt-4 flex items-center gap-2">
                <input
                  data-testid="guest-coach-input"
                  value={cInput}
                  onChange={(e) => setCInput(e.target.value)}
                  className="flex-1 bg-[#F9FAF7] border border-[#DCE5DB] rounded-full px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px]"
                  placeholder="Type your message…"
                  maxLength={800}
                />
                <button
                  type="submit"
                  data-testid="guest-coach-send"
                  disabled={cBusy || cInput.trim().length < 2}
                  className="inline-flex items-center justify-center bg-[#4F6F52] hover:bg-[#3F5A42] text-white disabled:opacity-60 rounded-full w-11 h-11 shrink-0 transition shadow-[0_4px_14px_rgba(79,111,82,0.22)]"
                >
                  {cBusy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
                </button>
              </form>
            </div>
          </section>
        )}

        {tab === "say-it-better" && (
          <SayItBetterPanel
            initialPrefill={saybPrefill}
            onSaved={() => toast.success("Draft ready to send.")}
          />
        )}

        {tab === "journal" && (
          <JournalScratchPanel
            draft={journalDraft}
            setDraft={setJournalDraft}
            list={journalList}
            onAdd={(row) => {
              const next = addJournalScratch(row);
              setJournalList(next?.journal_scratch || listJournalScratch());
              setJournalDraft({ title: "", content: "" });
            }}
            onRemove={(id) => {
              const next = removeJournalScratch(id);
              setJournalList(next?.journal_scratch || listJournalScratch());
            }}
          />
        )}

        {tab === "wisdom" && (
          <WisdomScratchPanel
            list={wisdomList}
            onRemove={(id) => {
              const next = removeWisdomScratch(id);
              setWisdomList(next?.wisdom_scratch || listWisdomScratch());
            }}
          />
        )}
      </main>

      <footer className="border-t border-[#DCE5DB] py-6 bg-white/50">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-[#6B6B6B] flex flex-wrap justify-center gap-x-5 gap-y-1">
          <Link to="/privacy" className="hover:text-[#4F6F52]">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-[#4F6F52]">
            Terms
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Header({ daysLeft, hoursLeft, onCTA, fromProverb }) {
  return (
    <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/welcome" className="flex items-center gap-2.5" data-testid="guest-home-link">
          <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
            <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
          </div>
          <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-4">
          {fromProverb && (
            <ReturnToProverb
              testid="guest-return-to-proverb"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#6B6B6B] hover:text-[#4F6F52] transition"
            />
          )}
          <div
            data-testid="guest-trial-header"
            className="text-xs sm:text-sm text-[#4F6F52] font-medium hidden sm:block"
          >
            3-Day Pro Trial · {daysLeft > 1 ? `${daysLeft} days` : `${hoursLeft} hrs`} left
          </div>
          <Link
            to="/signup?from=guest"
            data-testid="guest-header-signup"
            onClick={onCTA}
            className="text-xs sm:text-sm bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-3.5 sm:px-5 py-2 font-medium transition"
          >
            Create free account
          </Link>
        </div>
      </div>
    </header>
  );
}

function onSignupClick(state) {
  if (state?.id) {
    // Fire-and-forget; api layer swallows errors.
    guestApi.track({
      event_type: "guest_signup_click",
      guest_id: state.id,
      from_proverb: !!state.from_proverb,
    });
  }
}

function SayItBetterPanel({ initialPrefill, onSaved }) {
  const [audience, setAudience] = useState("General");
  const [message, setMessage] = useState(initialPrefill?.original || "");
  const [suggested, setSuggested] = useState(initialPrefill?.draft || "");
  const [prior, setPrior] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [chosen, setChosen] = useState("calm");

  useEffect(() => {
    // On first mount when a prefill is set from guidance, scroll to top.
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const t = message.trim();
    if (t.length < 4) {
      toast.error("Paste the message you're about to send.");
      return;
    }
    setBusy(true);
    try {
      const r = await analyzeApi.sample({
        message: t,
        audience,
        prior_message: prior || null,
      });
      setResult(r);
      // Say It Better completed — surface Save to My Phone (guest path too).
      try {
        window.dispatchEvent(new CustomEvent("pwa:request-save-prompt", {
          detail: { trigger: "say_it_better" },
        }));
      } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Say It Better needs a moment — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="guest-say-it-better-panel"
      className="rounded-3xl bg-white border border-[#DCE5DB] p-6 sm:p-8 shadow-[0_6px_28px_rgba(0,0,0,0.04)] space-y-5"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold">
        <Wand2 size={13} /> Say It Better
      </div>
      <p className="text-[15px] text-[#6B6B6B] leading-relaxed">
        Paste the message you're about to send and I'll show you how it will land — plus a
        gentler, clearer version.
      </p>
      {suggested && (
        <div
          data-testid="guest-sayb-suggested"
          className="rounded-2xl bg-[#EEF3EC] border border-[#DCE5DB] px-4 py-3.5"
        >
          <div className="text-xs uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1.5">
            LifeGuide's suggested draft
          </div>
          <p className="text-[15px] text-[#2E2E2E] italic leading-relaxed">"{suggested}"</p>
          <button
            type="button"
            data-testid="guest-sayb-use-suggested"
            onClick={() => setMessage(suggested)}
            className="mt-3 text-xs text-[#4F6F52] hover:text-[#3F5A42] font-medium underline underline-offset-2"
          >
            Use this as my starting point
          </button>
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm text-[#6B6B6B]">Who are you messaging?</label>
        <input
          data-testid="guest-sayb-audience"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          maxLength={60}
          placeholder="e.g. spouse, sibling, boss, friend"
          className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px]"
        />
        <label className="block text-sm text-[#6B6B6B]">
          What they said (optional)
        </label>
        <textarea
          data-testid="guest-sayb-prior"
          rows={2}
          value={prior}
          onChange={(e) => setPrior(e.target.value)}
          maxLength={800}
          placeholder="Paste their message so I can factor in the context."
          className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px] leading-relaxed resize-none"
        />
        <label className="block text-sm text-[#6B6B6B]">Your message</label>
        <textarea
          data-testid="guest-sayb-message"
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={1500}
          placeholder="Paste or write what you want to send."
          className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px] leading-relaxed resize-none"
        />
        <button
          type="submit"
          data-testid="guest-sayb-submit"
          disabled={busy || message.trim().length < 4}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-4 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
        >
          {busy ? (
            <>
              <Loader2 size={15} className="animate-spin" /> Reading it as they will…
            </>
          ) : (
            <>Rewrite this <ArrowRight size={15} strokeWidth={2} /></>
          )}
        </button>
      </form>

      {result && (
        <div data-testid="guest-sayb-result" className="space-y-4">
          <div className="rounded-2xl bg-[#F9FAF7] border border-[#DCE5DB] px-4 py-3.5">
            <div className="text-xs uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1.5">
              How it will likely land
            </div>
            <p className="text-[15px] text-[#2E2E2E] leading-relaxed">{result.how_it_lands}</p>
            <p className="text-xs text-[#6B6B6B] mt-2">
              Tone score {result.tone_score}/10 · escalation risk: {result.risk_of_escalation}
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-2">
              Rewrites
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {["calm", "direct", "warm", "biblical"].map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`guest-sayb-rewrite-tab-${k}`}
                  onClick={() => setChosen(k)}
                  className={`text-xs rounded-full px-3.5 py-1.5 font-medium transition ${
                    chosen === k
                      ? "bg-[#4F6F52] text-white"
                      : "bg-[#EEF3EC] text-[#3F5A42] hover:bg-[#DDE7DA] border border-[#DCE5DB]"
                  }`}
                >
                  {k[0].toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
            <div
              data-testid="guest-sayb-rewrite-body"
              className="rounded-2xl bg-white border border-[#DCE5DB] px-4 py-3.5 text-[15px] leading-relaxed text-[#2E2E2E] whitespace-pre-line"
            >
              {(result.rewrites || {})[chosen] || "—"}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function JournalScratchPanel({ draft, setDraft, list, onAdd, onRemove }) {
  return (
    <section
      data-testid="guest-journal-panel"
      className="space-y-5 rounded-3xl bg-white border border-[#DCE5DB] p-6 sm:p-8 shadow-[0_6px_28px_rgba(0,0,0,0.04)]"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold">
        <NotebookPen size={13} /> Journal · scratch
      </div>
      <p className="text-[15px] text-[#6B6B6B] leading-relaxed">
        Entries you draft here stay in this browser. Create a free account to save them across
        devices — your drafts will move over automatically.
      </p>
      <div className="space-y-3">
        <input
          data-testid="guest-journal-title"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Title (optional)"
          className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px]"
        />
        <textarea
          data-testid="guest-journal-content"
          rows={5}
          value={draft.content}
          onChange={(e) => setDraft({ ...draft, content: e.target.value })}
          placeholder="Write freely. This is between you and the page."
          className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px] leading-relaxed resize-none"
        />
        <button
          type="button"
          data-testid="guest-journal-save"
          onClick={() => draft.content.trim() && onAdd(draft)}
          disabled={draft.content.trim().length < 2}
          className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full px-5 py-2.5 text-sm font-medium transition"
        >
          <NotebookPen size={13} /> Save to Journal scratch
        </button>
      </div>
      <div className="pt-3 border-t border-[#DCE5DB]/70 space-y-3" data-testid="guest-journal-list">
        {list.length === 0 && (
          <div className="text-sm text-[#6B6B6B] italic">
            No entries yet. Write something above — even a sentence counts.
          </div>
        )}
        {list.map((r) => (
          <div key={r.id} className="rounded-2xl border border-[#DCE5DB] px-4 py-3 bg-white">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm font-semibold text-[#2E4531]">{r.title || "Untitled"}</div>
              <button
                type="button"
                data-testid={`guest-journal-remove-${r.id}`}
                onClick={() => onRemove(r.id)}
                className="text-[#8C7A4F] hover:text-[#A86B4A]"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="text-[13px] text-[#6B6B6B] whitespace-pre-line mt-1.5">
              {r.content}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WisdomScratchPanel({ list, onRemove }) {
  return (
    <section
      data-testid="guest-wisdom-panel"
      className="space-y-5 rounded-3xl bg-white border border-[#DCE5DB] p-6 sm:p-8 shadow-[0_6px_28px_rgba(0,0,0,0.04)]"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold">
        <BookmarkPlus size={13} /> Saved Wisdom · scratch
      </div>
      <p className="text-[15px] text-[#6B6B6B] leading-relaxed">
        Tap "Save this wisdom" on any guidance response to keep it here for the next 3 days.
        Create a free account to save these permanently across devices.
      </p>
      {list.length === 0 && (
        <div className="text-sm text-[#6B6B6B] italic">
          Nothing saved yet. Try Immediate Guidance and use "Save this wisdom".
        </div>
      )}
      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.id} className="rounded-2xl border border-[#DCE5DB] px-4 py-3 bg-white">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm font-semibold text-[#2E4531]">{r.title || "Untitled"}</div>
              <button
                type="button"
                data-testid={`guest-wisdom-remove-${r.id}`}
                onClick={() => onRemove(r.id)}
                className="text-[#8C7A4F] hover:text-[#A86B4A]"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="text-[13px] text-[#2E2E2E] whitespace-pre-line mt-1.5">{r.body}</div>
            {r.reference && (
              <div className="text-xs italic text-[#4F6F52] mt-1.5">— {r.reference}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function TrialEndedGate() {
  return (
    <div className="min-h-screen text-[#2E2E2E]" data-testid="guest-trial-ended">
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
              <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
          </Link>
        </div>
      </header>
      <main className="max-w-lg mx-auto px-6 py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#F5EBD9] flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={26} className="text-[#8C7A4F]" strokeWidth={1.8} />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3 text-[#2E4531]">
          Your 3-day trial ended.
        </h1>
        <p className="text-[#6B6B6B] leading-relaxed mb-8">
          Create a free account to keep going. Your journal drafts, saved wisdom, and history
          come with you.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/signup?from=guest"
            data-testid="guest-trial-ended-signup"
            className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3.5 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
          >
            Create free account <ArrowRight size={14} strokeWidth={2} />
          </Link>
          <Link
            to="/login"
            className="text-sm text-[#6B6B6B] hover:text-[#4F6F52] px-4 py-2"
          >
            I already have an account
          </Link>
        </div>
      </main>
    </div>
  );
}
